import styled from 'styled-components';
import { AchievementBadge, AchievementBadgeProps } from './AchievementBadge';
import {
	ACHIEVEMENT_BADGE_SIZES_PX as SIZES_PX, AchievementBadgeSize, getAchievementColorTheme,
} from './achievementBadgeTokens';

export type BadgeStackAchievement = Pick<AchievementBadgeProps, 'iconId' | 'color' | 'shape'>;

export type AchievementBadgeStackProps = Pick<AchievementBadgeProps, 'size' | 'variant' | 'baseUrl' | 'className'> & {
	/** Every badge of one achievement type. More than one draws two offset badges and a count. */
	achievements: BadgeStackAchievement[];
}

const MAX_COUNT = 99;

// Per size: badge offset (x, y for square), counter diameter, font, and corner offset (top, right).
type StackGeometry = { x: number; y: number; dot: number; font: number; top: number; right: number };

const STACK: Record<AchievementBadgeSize, StackGeometry> = {
	small: { x: 3, y: 2, dot: 15, font: 8, top: -2, right: -5 },
	medium: { x: 4, y: 3, dot: 20, font: 9, top: -3, right: -3 },
	large: { x: 5, y: 4, dot: 26, font: 11, top: -4, right: 1 },
};

// The offset badges and the counter paint past the box; the margins claim that overhang
// so a layout spaces the stack by what is visible, not by its box.
const StackedContainer = styled.div<{ $size: AchievementBadgeSize }>`
	position: relative;
	flex-shrink: 0;
	width: ${props => SIZES_PX[props.$size]}px;
	height: ${props => SIZES_PX[props.$size]}px;
	margin-left: ${props => STACK[props.$size].x}px;
	margin-right: ${props => Math.max(STACK[props.$size].x, -STACK[props.$size].right)}px;
`;

const OffsetBadge = styled(AchievementBadge)<{ $x: number; $y: number; $shadow?: string }>`
	position: absolute;
	left: ${props => props.$x}px;
	top: ${props => props.$y}px;
	${props => props.$shadow && `filter: drop-shadow(${props.$shadow} 0 rgba(0, 0, 0, 0.2));`}
`;

const Counter = styled.div<{ $size: AchievementBadgeSize; $color: string; $background: string }>`
	position: absolute;
	top: ${props => STACK[props.$size].top}px;
	right: ${props => STACK[props.$size].right}px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${props => STACK[props.$size].dot}px;
	height: ${props => STACK[props.$size].dot}px;
	border-radius: 50%;
	background: ${props => props.$background};
	color: ${props => props.$color};
	font-size: ${props => STACK[props.$size].font}px;
	font-weight: 600;
`;

export function AchievementBadgeStack({
	achievements, size = 'large', variant = 'dark', baseUrl, className,
}: AchievementBadgeStackProps) {
	const { iconId, color, shape } = achievements[0];
	const badge = { iconId, color, shape, size, variant, baseUrl };

	if (achievements.length < 2) {
		return <AchievementBadge {...badge} className={className} />;
	}

	const { x } = STACK[size];
	const square = shape === 'square';
	const y = square ? STACK[size].y : 0;
	const { counter } = getAchievementColorTheme(color, variant);

	return <StackedContainer $size={size} className={className}>
		<OffsetBadge {...badge} $x={x} $y={-y} />
		<OffsetBadge {...badge} $x={-x} $y={y} $shadow={square ? '2px -1px' : '2px 1px'} />
		<Counter $size={size} $color={counter.color} $background={counter.background}>
			{achievements.length > MAX_COUNT ? `${MAX_COUNT}+` : achievements.length}
		</Counter>
	</StackedContainer>;
}
